/** @jsx jsx */
import React, { useState, useCallback } from 'react';
import { jsx } from '@emotion/core';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';

import catchOnly from 'utils/catchOnly';

import EmailDisclosureModalView from './EmailDisclosureModalView';
import FmcForm from 'components/FmcForm';

import useEmailDisclosure, {
  EmailType,
  EmailDisclosureError,
} from 'hooks/requests/emailDisclosure';

import { Trans } from 'react-i18next';
import { Status } from 'hooks/requests/fetchUtils';
import { NonClientError } from 'hooks/requests/fetchUtils';

import styles from './styles';
import Button from 'components/Button';

const schema = Yup.object().shape({
  emailAddress: Yup.string()
    .label('Email')
    .nullable()
    .required()
    .email('must be a valid email address'),
});

export type FormValues = Yup.InferType<typeof schema>;

const defaultButton = ({
  onClick,
  loading,
}: {
  onClick: () => void;
  loading: boolean;
}): React.ReactElement => {
  return (
    <Button
      id="reviewTermsEmailDisclosures"
      css={styles.emailLink}
      onClick={onClick}
      variant="text"
      loading={loading}
    >
      <Trans i18nKey="emailDisclosures.emailButton" />
    </Button>
  );
};

interface PropTypes {
  email?: string | null;
  sendEmail: (email: string) => Promise<unknown>;
  emailType: EmailType;
  children?: (props: { onClick: () => void }) => React.ReactElement;
}

const EmailDisclosureButton: React.FC<PropTypes> = ({
  email,
  children,
  emailType,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const childRender = children || defaultButton;
  const { enqueueSnackbar } = useSnackbar();

  const [sendEmail, status] = useEmailDisclosure();

  const send = useCallback(
    async (email: string) => {
      try {
        await sendEmail(email, emailType);
        enqueueSnackbar('Email has been sent!', {
          variant: 'success',
          persist: true,
        });
        setIsOpen(false);
      } catch (e) {
        if (catchOnly(e, NonClientError, EmailDisclosureError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [enqueueSnackbar, sendEmail, emailType],
  );

  const onOpen = useCallback(() => {
    if (email) {
      send(email);
    } else {
      setIsOpen(true);
    }
  }, [send, email]);

  const onSubmit = useCallback(
    async (formValue: FormValues) => {
      await send(formValue.emailAddress as string);
    },
    [send],
  );

  const reset = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <React.Fragment>
      {childRender({ onClick: onOpen, loading: status === Status.IN_PROGRESS })}

      {!email && isOpen && (
        <FmcForm
          validationSchema={schema}
          initialValues={{ emailAddress: '' }}
          onSubmit={onSubmit}
        >
          {props => <EmailDisclosureModalView {...props} onClose={reset} />}
        </FmcForm>
      )}
    </React.Fragment>
  );
};

export default EmailDisclosureButton;
